//* External
import { Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Row } from 'react-bootstrap';

//* App Custom
import {
  Button,
  DataMapper,
  DynamicFormSection,
  InputHandler,
  Wrapper
} from '@componentsApp/common';
import { tnl } from '@i18nApp/i18n';
import {
  patientInfoObtained,
  updateContactInformationInputs
} from './updatePatientContactModalData';
import {
  createPeopleEmail,
  createPeoplePhone,
  deletePeoplePhone,
  updatePeopleEmail,
  updatePeoplePhone
} from './updatePatientContactModalRequests';

const UpdatePatientContactModal = ({
  contactInfo,
  onHide = () => {},
  onSuccess = () => {}
}) => {
  const { phones = [], emails = [], patient = {} } = contactInfo;

  const { isDoingRequest } = useSelector((state) => state.commonState);

  const { control, formState, handleSubmit, watch } = useForm({
    mode: 'all',
    defaultValues: {
      phones: phones.length > 0 ? phones : [{ number_type: '', number: '' }],
      emails: emails.length > 0 ? emails : [{ email: '', pk: '' }]
    }
  });

  const handleUpdateContact = async (data) => {
    const { emails = [], phones = [] } = data;

    const emailPromises = emails
      .filter((e) => e && e.email)
      .filter((e) => {
        const prev = contactInfo.emails.find((old) => old.pk === e.pk);
        return !prev || prev.email !== e.email;
      })
      .map((e) =>
        e.pk
          ? updatePeopleEmail({
              data: {
                pk: e.pk,
                email: e.email,
                patient: e.patient || patient.pk
              }
            })
          : createPeopleEmail({
              data: { email: e.email, patient: patient.pk }
            })
      );

    const phonePromises = phones
      .filter((p) => p && p.number)
      .filter((p) => {
        const prev = contactInfo.phones.find((old) => old.pk === p.pk);
        return (
          !prev ||
          prev.number !== p.number ||
          prev.number_type !== p.number_type
        );
      })
      .map((p) =>
        p.pk
          ? updatePeoplePhone({
              data: { ...p, patient: p.patient || patient.pk }
            })
          : createPeoplePhone({
              data: { ...p, patient: patient.pk }
            })
      );

    await Promise.all([...emailPromises, ...phonePromises]);
    onHide();
    onSuccess();
  };

  return (
    <Modal
      show
      onHide={onHide}
      backdrop="static"
      keyboard={false}
      size="lg"
      scrollable
    >
      <Modal.Header closeButton>
        <h4>{tnl('buttons.update_contact_information')}</h4>
      </Modal.Header>
      <form
        onSubmit={handleSubmit(handleUpdateContact)}
        style={{ overflowY: 'auto' }}
      >
        <Modal.Body>
          <div className="mb-3">
            <Wrapper title="labels.general_data">
              <DataMapper data={patientInfoObtained({ contactInfo })} />
            </Wrapper>
          </div>
          <div className="mb-3">
            <Wrapper title={phones.length ? '' : 'labels.phones'}>
              <DynamicFormSection
                name="phones"
                control={control}
                watch={watch}
                onRemoveItem={(item) =>
                  deletePeoplePhone({
                    data: item,
                    onSuccess: () => {
                      onHide();
                      onSuccess();
                    }
                  })
                }
              />
            </Wrapper>
          </div>
          <div className="mb-3">
            <Wrapper title="labels.emails">
              <Row>
                {updateContactInformationInputs({ contactInfo }).map(
                  (input) => (
                    <InputHandler
                      key={input.name}
                      {...input}
                      control={control}
                    />
                  )
                )}
              </Row>
            </Wrapper>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            text="buttons.close"
            onClick={onHide}
            disabled={isDoingRequest}
          />
          <Button
            type="submit"
            text="buttons.update"
            title={!formState.isValid ? 'titles.missing_required_inputs' : ''}
            disabled={!formState.isValid}
            isDoingRequest={isDoingRequest}
          />
        </Modal.Footer>
      </form>
    </Modal>
  );
};

UpdatePatientContactModal.propTypes = {};

export default UpdatePatientContactModal;
